/**
 * DreamScanForm: the nightly cap, the traffic threshold, the run days and
 * the scan rules, saved whole. "Test rules" previews the draft against the team's observed traces
 * without writing anything, and sits next to the rules it reads so the
 * forecast appears where the click was.
 *
 * Run picking (TRA-1511): a switch between the by-job pick and the old
 * random pick, shown to TrainLoop staff only (`showSelection`). The form
 * sends the draft's value back whole, so a customer's save keeps what
 * staff set.
 *
 * Second review limits (TRA-1569 patch 4): three per-team numbers, shown
 * to TrainLoop staff only (`showSelection` again). A customer's save
 * leaves them out of the body and out of the Save button's validation,
 * and the API keeps the stored values for a customer either way; empty
 * means the global number.
 *
 * Run days (TRA-1078): the cron fires every night, and the scan skips a
 * team whose traffic day is unticked (Cartage: hardly anyone uses Wilson on
 * weekends). Traffic from an unticked day is never judged, not even later.
 */

import { useState, type FormEvent } from 'react'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'

import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import type {
  DreamScanFieldValues,
  DreamScanRuleField,
  DreamScanRuleTestResponse,
  DreamScanSettings,
  DreamScanSettingsSelection,
} from '@/api/generated/model'
import { SettingsFormFooter } from '@/components/settings/SettingsFormFooter'
import { SettingsSection } from '@/components/settings/SettingsSection'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  useDreamScanFieldValues,
  useTestDreamScanRules,
  useUpdateDreamScan,
} from '@/hooks/useTeamSettings'

import { RuleTestPreview } from './RuleTestPreview'
import { RUN_DAY_LABELS, RUN_DAYS, type RunDay } from './runDays'
import { ScanRuleRow } from './ScanRuleRow'
import { CAP_MAX, rulesKey, saveBody, trimRules } from './scanRules'
import {
  CUT_SIZE_MAX,
  QUEUE_DEPTH_MAX,
  globalLimitsText,
  type LimitErrors,
  type LimitName,
  type TeamLimitsDraft,
} from './teamLimits'
import { useDreamScanDraft, type DreamScanDraftState } from './useDreamScanDraft'

interface RuleTest {
  /** rulesKey of the rules the forecast was computed from. */
  key: string
  fields: DreamScanRuleField[]
  result: DreamScanRuleTestResponse
}

/** A 400 or 422 detail belongs under the form; anything else is a toast. */
function isFieldError(error: unknown): boolean {
  const status = getApiErrorStatus(error)
  return status === 422 || status === 400
}

export function DreamScanForm({
  settings,
  readOnly,
  showSelection,
}: {
  settings: DreamScanSettings
  readOnly: boolean
  /** TrainLoop staff see the run picking switch; customers do not. */
  showSelection: boolean
}): React.JSX.Element {
  const valuesQuery = useDreamScanFieldValues()
  const saveMutation = useUpdateDreamScan()
  const testMutation = useTestDreamScanRules()
  const form = useDreamScanDraft(settings, showSelection)
  const { rules, runDays, selection, limits } = form.draft
  const [fieldError, setFieldError] = useState<string | null>(null)
  // A forecast is only valid for the exact rules it was computed from, so
  // it is stored WITH those rules' key and rendered only while the current
  // rules still match: edits hide it and stale responses never show.
  const [test, setTest] = useState<RuleTest | null>(null)
  const canSubmit = !readOnly && !saveMutation.isPending && form.dirty && form.valid

  const showError = (error: unknown, fallback: string): void => {
    const detail = getApiErrorDetail(error) ?? fallback
    if (isFieldError(error)) {
      setFieldError(detail)
    } else {
      toast.error(detail)
    }
  }

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (!canSubmit) return
    setFieldError(null)
    saveMutation.mutate(
      { data: saveBody(form.draft, showSelection) },
      {
        onSuccess: () => {
          toast.success('Review-scan settings saved')
        },
        onError: (error) => {
          showError(error, 'Failed to save review-scan settings')
        },
      },
    )
  }

  const handleTest = (): void => {
    setFieldError(null)
    const draft = trimRules(rules)
    testMutation.mutate(
      { data: { rules: draft } },
      {
        onSuccess: (response) => {
          if (response.status === 200) {
            setTest({
              key: rulesKey(draft),
              fields: [...new Set(draft.map((rule) => rule.field))],
              result: response.data,
            })
          }
        },
        onError: (error) => {
          setTest(null)
          showError(error, 'Failed to test the rules')
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* One disabled fieldset turns every control off under impersonation. */}
      <fieldset disabled={readOnly} className="min-w-0">
        <SettingsSection
          title="Nightly cap"
          description="The judge reads this many of the day's trajectories each night."
        >
          <CapField
            value={form.draft.capText}
            shippedDefault={settings.shipped_default}
            error={form.errors.cap}
            onChange={form.setCapText}
          />
        </SettingsSection>

        {showSelection && (
          <SettingsSection
            title="Second review limits"
            description="Per-team limits for the second review. Empty uses the global number. Only TrainLoop staff see these."
          >
            <TeamLimitsFields
              limits={limits}
              globals={globalLimitsText(settings)}
              errors={form.errors.limits}
              onChange={form.setLimit}
            />
          </SettingsSection>
        )}

        <SettingsSection
          title="Run days"
          description="The scan runs each night at 10 PM Pacific and judges that day's traffic. Flags reach the review queue the next morning."
        >
          <RunDaysPicker days={runDays} error={form.errors.days} onToggle={form.toggleDay} />
        </SettingsSection>

        {showSelection && (
          <SettingsSection
            title="Run picking"
            description="How the judge's runs are picked each night. Only TrainLoop staff see this switch."
          >
            <SelectionField selection={selection} onChange={form.setSelection} />
          </SettingsSection>
        )}

        <SettingsSection
          title="Scan rules"
          description="Exclude rules skip matching trajectories and win over include rules. Include rules limit the scan to trajectories matching one of them on that field. Filtered-out traces keep being collected and their existing flags stay reviewable. Regex must match the whole value."
        >
          <ScanRulesEditor
            form={form}
            suggestions={valuesQuery.data}
            suggestionsFailed={valuesQuery.isError}
            test={test !== null && test.key === rulesKey(rules) ? test : null}
            testPending={testMutation.isPending}
            onTest={handleTest}
          />
        </SettingsSection>

        <SettingsFormFooter dirty={form.dirty} error={fieldError}>
          <Button
            type="button"
            variant="outline"
            disabled={!form.dirty || saveMutation.isPending}
            onClick={() => {
              form.discard()
              setFieldError(null)
            }}
          >
            Discard
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            Save
          </Button>
        </SettingsFormFooter>
      </fieldset>
    </form>
  )
}

function CapField({
  value,
  shippedDefault,
  error,
  onChange,
}: {
  value: string
  shippedDefault: number
  error: string | null
  onChange: (value: string) => void
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      <Label htmlFor="dream-scan-cap">Trajectories per night</Label>
      <Input
        id="dream-scan-cap"
        className="w-40 font-mono"
        type="number"
        min={0}
        max={CAP_MAX}
        step={1}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        placeholder={`default (${String(shippedDefault)})`}
        aria-describedby="dream-scan-cap-hint"
      />
      <p id="dream-scan-cap-hint" className="text-xs text-muted-foreground">
        Leave it empty for the default of {shippedDefault}, or set 0 to pause the scan.
      </p>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

interface LimitBox {
  name: LimitName
  label: string
  hint: string
  max: number
  /** 'any' on the share: the API stores any fraction, and a stored
   *  12.55% must not fail the browser's own step check at submit time. */
  step: number | 'any'
}

const LIMIT_BOXES: readonly LimitBox[] = [
  {
    name: 'queueDepth',
    label: 'People queue size',
    hint: 'Flags waiting for a person at once.',
    max: QUEUE_DEPTH_MAX,
    step: 1,
  },
  {
    name: 'cutSize',
    label: 'Nightly cut',
    hint: 'Flags kept a night for people and tests together.',
    max: CUT_SIZE_MAX,
    step: 1,
  },
  {
    name: 'goodShare',
    label: 'Good-flag share (%)',
    hint: 'Share of the cut kept for good flags.',
    max: 100,
    step: 'any',
  },
]

function TeamLimitsFields({
  limits,
  globals,
  errors,
  onChange,
}: {
  limits: TeamLimitsDraft
  /** The global numbers as the boxes show them, for the placeholder and hint. */
  globals: TeamLimitsDraft
  errors: LimitErrors
  onChange: (name: LimitName, text: string) => void
}): React.JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {LIMIT_BOXES.map((box) => {
        const id = `dream-scan-limit-${box.name}`
        const error = errors[box.name]
        return (
          <div key={box.name} className="space-y-2">
            <Label htmlFor={id}>{box.label}</Label>
            <Input
              id={id}
              className="w-40 font-mono"
              type="number"
              min={0}
              max={box.max}
              step={box.step}
              value={limits[box.name]}
              onChange={(e) => {
                onChange(box.name, e.target.value)
              }}
              placeholder={globals[box.name]}
              aria-describedby={`${id}-hint`}
              aria-invalid={error !== null}
            />
            <p id={`${id}-hint`} className="text-xs text-muted-foreground">
              {box.hint} Empty uses the global number ({globals[box.name]}).
            </p>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}

function SelectionField({
  selection,
  onChange,
}: {
  selection: DreamScanSettingsSelection
  onChange: (selection: DreamScanSettingsSelection) => void
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Switch
          id="dream-scan-by-job"
          checked={selection === 'jobs'}
          aria-describedby="dream-scan-by-job-hint"
          onCheckedChange={(checked) => {
            onChange(checked ? 'jobs' : 'legacy')
          }}
        />
        <Label htmlFor="dream-scan-by-job">Pick runs by job</Label>
      </div>
      <p id="dream-scan-by-job-hint" className="text-xs text-muted-foreground">
        The scan drops copies and splits the nightly cap across the team's jobs. Turn it off to
        pick runs at random, as the scan did before. A team without a job map picks at random
        either way.
      </p>
    </div>
  )
}

/** One hairline or solid chip per weekday; solid means the scan runs that night. */
function RunDaysPicker({
  days,
  error,
  onToggle,
}: {
  days: readonly RunDay[]
  error: string | null
  onToggle: (day: RunDay) => void
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      <p id="dream-scan-run-days-label" className="text-sm font-medium leading-none text-foreground">
        Days scanned
      </p>
      <div
        className="flex flex-wrap gap-1"
        role="group"
        aria-labelledby="dream-scan-run-days-label"
        aria-describedby="dream-scan-run-days-hint"
      >
        {RUN_DAYS.map((day) => {
          const on = days.includes(day)
          return (
            <Button
              key={day}
              type="button"
              size="sm"
              variant={on ? 'default' : 'outline'}
              aria-pressed={on}
              className="min-w-[3.25rem]"
              onClick={() => {
                onToggle(day)
              }}
            >
              {RUN_DAY_LABELS[day]}
            </Button>
          )
        })}
      </div>
      {error ? (
        <p id="dream-scan-run-days-hint" className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : (
        <p id="dream-scan-run-days-hint" className="text-xs text-muted-foreground">
          The scan runs {days.length} of 7 nights. Traffic from an unticked night is never
          judged, not even later.
        </p>
      )}
    </div>
  )
}

function ScanRulesEditor({
  form,
  suggestions,
  suggestionsFailed,
  test,
  testPending,
  onTest,
}: {
  form: DreamScanDraftState
  suggestions: DreamScanFieldValues | undefined
  suggestionsFailed: boolean
  test: RuleTest | null
  testPending: boolean
  onTest: () => void
}): React.JSX.Element {
  const { rules } = form.draft
  return (
    <div className="space-y-3">
      {rules.some((rule) => rule.mode === 'include') && (
        <p className="text-xs text-warning">
          Include rules drop traces that never sent the field: a trace with no metadata cannot
          match, so it is not scanned. Exclude rules keep such traces.
        </p>
      )}
      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground">There are no rules, so every trajectory is eligible for the scan.</p>
      ) : (
        <ul className="space-y-2">
          {rules.map((rule, index) => (
            <ScanRuleRow
              key={index}
              index={index}
              rule={rule}
              suggestions={suggestions?.[rule.field] ?? []}
              onChange={(next) => {
                form.setRules((prev) => prev.map((r, i) => (i === index ? next : r)))
              }}
              onRemove={() => {
                form.setRules((prev) => prev.filter((_, i) => i !== index))
              }}
            />
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="outline" size="sm" onClick={form.addRule}>
          <Plus />
          Add rule
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={testPending || form.errors.rules !== null}
          onClick={onTest}
        >
          {testPending ? 'Testing...' : 'Test rules'}
        </Button>
        {suggestionsFailed && (
          <p className="text-xs text-muted-foreground">
            Value suggestions failed to load. Rules can still be typed by hand.
          </p>
        )}
      </div>
      {test !== null && <RuleTestPreview result={test.result} testedFields={test.fields} />}
      {form.errors.rules && (
        <p className="text-sm text-destructive" role="alert">
          {form.errors.rules}
        </p>
      )}
    </div>
  )
}
